/**
 * DeadlineAlertsPanel Component
 *
 * Pannello per visualizzare e gestire gli alert di scadenza task.
 * Supporta snooze e dismiss degli alert.
 */

import React, { useState } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  ActionIcon,
  Menu,
  Tooltip,
  Avatar,
  Collapse,
  NumberInput,
  Alert as MantineAlert,
  Loader,
} from '@mantine/core';
import {
  IconBell,
  IconBellOff,
  IconClock,
  IconCalendar,
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp,
  IconSettings,
  IconX,
} from '@tabler/icons-react';
import { format, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import type { DeadlineAlert, UserAlertPreferences } from '../../types/api';
import {
  useDeadlineAlerts,
  useSnoozeAlert,
  useDismissAlert,
  useAlertPreferences,
  useUpdateAlertPreferences,
} from '../../hooks/use-kanban-columns';

interface DeadlineAlertsPanelProps {
  onTaskClick?: (taskId: number) => void;
}

const DeadlineAlertsPanel: React.FC<DeadlineAlertsPanelProps> = ({
  onTaskClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [alertDays, setAlertDays] = useState<number | undefined>(3);

  const { data: alerts, isLoading: alertsLoading } = useDeadlineAlerts();
  const { data: preferences } = useAlertPreferences();
  const snoozeAlert = useSnoozeAlert();
  const dismissAlert = useDismissAlert();
  const updatePreferences = useUpdateAlertPreferences();

  React.useEffect(() => {
    if (preferences) {
      setAlertDays(preferences.deadlineAlertDays);
    }
  }, [preferences]);

  const handleSnooze = (taskId: number, minutes: number) => {
    snoozeAlert.mutate({ taskId, snoozeMinutes: minutes });
  };

  const handleDismiss = (taskId: number) => {
    dismissAlert.mutate({ taskId });
  };

  const handleSavePreferences = () => {
    if (alertDays !== undefined) {
      updatePreferences.mutate({ deadlineAlertDays: alertDays });
      setShowSettings(false);
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'orange';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  if (alertsLoading) {
    return (
      <Paper p="md" withBorder>
        <Group justify="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Caricamento alert...</Text>
        </Group>
      </Paper>
    );
  }

  if (!alerts || alerts.length === 0) {
    return null; // Non mostrare il pannello se non ci sono alert
  }

  const overdueAlerts = alerts.filter(a => a.isOverdue);
  const upcomingAlerts = alerts.filter(a => !a.isOverdue);

  return (
    <Paper p="md" withBorder mb="md">
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between">
          <Group
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <IconBell size={20} color={overdueAlerts.length > 0 ? '#fa5252' : '#fab005'} />
            <Text fw={600}>
              Alert Scadenze
              <Badge ml="xs" color={overdueAlerts.length > 0 ? 'red' : 'yellow'} size="sm" style={{ color: 'white' }}>
                {alerts.length}
              </Badge>
            </Text>
            {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Group>

          <Tooltip label="Impostazioni alert">
            <ActionIcon
              variant="subtle"
              onClick={() => setShowSettings(!showSettings)}
            >
              <IconSettings size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Settings Panel */}
        <Collapse in={showSettings}>
          <Paper p="sm" bg="gray.0" withBorder>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Preferenze Alert</Text>
              <Group>
                <NumberInput
                  label="Giorni anticipo"
                  description="Ricevi alert X giorni prima della scadenza"
                  value={alertDays}
                  onChange={(v) => setAlertDays(typeof v === 'number' ? v : undefined)}
                  min={1}
                  max={30}
                  style={{ width: 150 }}
                />
                <Button
                  size="xs"
                  onClick={handleSavePreferences}
                  loading={updatePreferences.isPending}
                  mt="xl"
                >
                  Salva
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Collapse>

        {/* Alerts List */}
        <Collapse in={isExpanded}>
          <Stack gap="xs">
            {/* Overdue Alerts */}
            {overdueAlerts.length > 0 && (
              <>
                <Text size="xs" fw={600} c="red" tt="uppercase">
                  Scaduti ({overdueAlerts.length})
                </Text>
                {overdueAlerts.map((alert) => (
                  <AlertItem
                    key={alert.taskId}
                    alert={alert}
                    onTaskClick={onTaskClick}
                    onSnooze={handleSnooze}
                    onDismiss={handleDismiss}
                    getPriorityColor={getPriorityColor}
                  />
                ))}
              </>
            )}

            {/* Upcoming Alerts */}
            {upcomingAlerts.length > 0 && (
              <>
                <Text size="xs" fw={600} c="yellow.8" tt="uppercase" mt={overdueAlerts.length > 0 ? 'xs' : 0}>
                  In scadenza ({upcomingAlerts.length})
                </Text>
                {upcomingAlerts.map((alert) => (
                  <AlertItem
                    key={alert.taskId}
                    alert={alert}
                    onTaskClick={onTaskClick}
                    onSnooze={handleSnooze}
                    onDismiss={handleDismiss}
                    getPriorityColor={getPriorityColor}
                  />
                ))}
              </>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};

// Alert Item Component
interface AlertItemProps {
  alert: DeadlineAlert;
  onTaskClick?: (taskId: number) => void;
  onSnooze: (taskId: number, minutes: number) => void;
  onDismiss: (taskId: number) => void;
  getPriorityColor: (priority: string) => string;
}

const AlertItem: React.FC<AlertItemProps> = ({
  alert,
  onTaskClick,
  onSnooze,
  onDismiss,
  getPriorityColor,
}) => {
  const dueDate = alert.internalDueDate || alert.dueDate;

  return (
    <Paper
      p="xs"
      withBorder
      style={{
        borderColor: alert.isOverdue ? '#fa5252' : '#fab005',
        borderLeftWidth: 3,
        cursor: onTaskClick ? 'pointer' : 'default',
      }}
      onClick={() => onTaskClick?.(alert.taskId)}
    >
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            {alert.isOverdue && <IconAlertTriangle size={14} color="#fa5252" />}
            <Text
              size="sm"
              fw={500}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {alert.taskTitle}
            </Text>
            <Badge size="xs" color={getPriorityColor(alert.priority)} style={{ color: 'white' }}>
              {alert.priority}
            </Badge>
          </Group>

          <Group gap="xs">
            <IconCalendar size={12} color="#868e96" />
            <Text size="xs" c={alert.isOverdue ? 'red' : 'dimmed'}>
              {alert.isOverdue
                ? `Scaduto ${formatDistanceToNow(new Date(dueDate), { locale: it, addSuffix: true })}`
                : `Scade ${format(new Date(dueDate), 'dd/MM/yyyy', { locale: it })}`}
            </Text>
          </Group>

          {alert.assignedTo && (
            <Group gap="xs">
              <Avatar size="xs" color={alert.assignedTo.colorTag || 'blue'}>
                {alert.assignedTo.fullName
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </Avatar>
              <Text size="xs" c="dimmed">{alert.assignedTo.fullName}</Text>
            </Group>
          )}
        </Stack>

        <Group gap={4} onClick={(e) => e.stopPropagation()}>
          <Menu position="bottom-end">
            <Menu.Target>
              <Tooltip label="Posticipa alert">
                <ActionIcon variant="subtle" size="sm">
                  <IconClock size={14} />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Posticipa per...</Menu.Label>
              <Menu.Item onClick={() => onSnooze(alert.taskId, 60)}>1 ora</Menu.Item>
              <Menu.Item onClick={() => onSnooze(alert.taskId, 240)}>4 ore</Menu.Item>
              <Menu.Item onClick={() => onSnooze(alert.taskId, 1440)}>1 giorno</Menu.Item>
              <Menu.Item onClick={() => onSnooze(alert.taskId, 4320)}>3 giorni</Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Tooltip label="Rimuovi alert">
            <ActionIcon
              variant="subtle"
              size="sm"
              color="red"
              onClick={() => onDismiss(alert.taskId)}
            >
              <IconBellOff size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Paper>
  );
};

export default DeadlineAlertsPanel;
